<script setup>
import { ref } from "vue";
import BreezeAuthenticatedLayout from '@/Layouts/Authenticated.vue';
import { Head, Link, useForm } from '@inertiajs/inertia-vue3';
import { Inertia } from '@inertiajs/inertia';
import { Icon } from "@iconify/vue";
import Dropdown from "@/Components/Dropdown.vue";
import Tabs from "@/Components/Tabs.vue";
import Buttons from "@/Components/Buttons.vue";
import ModalAlert from "@/Components/ModalAlert.vue";
import PageHeader from "@/Components/PageHeader.vue";

const props = defineProps({
    complaint: Object,
    fromEmail: String,
});

const form = useForm({
    id: props.complaint.id,
    subject: 'Respuesta a su Reclamo/Queja ' + props.complaint.correlative,
    message: ''
});

const isAlertActive = ref(false)
const isAlertDangerActive = ref(false)
const messageAlert = ref('')

const showModalAlert = ref(false)
const modalAlertTitle = ref('')
const modalAlertMessage = ref('')
const modalAlertType = ref('success')

const showAlert = (message, color = 'success') => {
    messageAlert.value = message;
    if (color === 'success') {
        isAlertActive.value = true;
    } else {
        isAlertDangerActive.value = true;
    }
    setTimeout(() => {
        isAlertActive.value = false;
        isAlertDangerActive.value = false;
    }, 4000);
}

const updateStatus = async (status) => {
    try {
        const response = await axios.post(route('ecommerceclaim-updatestatus'), {
            id: props.complaint.id,
            status: status
        });
        if (response.data.success) {
            props.complaint.status = status;
            showAlert(response.data.message, 'success');
            Inertia.reload({ only: ['complaint'] });
        } else {
            showAlert(response.data.message, 'danger');
        }
    } catch (error) {
        showAlert('Error al actualizar el estado.', 'danger');
    }
}

const sendEmail = () => {
    form.post(route('ecommerceclaim-sendemail'), {
        preserveScroll: true,
        onSuccess: (page) => {
            // El controlador devuelve JSON. Cuando usamos Inertia para rutas que no retornan vista, 
            // es mejor usar axios en este caso, o adaptar Inertia si devuelve redirect.
            // Para mantener la simplicidad y ya que es una API de JSON, usaremos axios:
        }
    });
};

const attachment = ref(null);
const fileInput = ref(null);

const handleFileChange = (e) => {
    if (e.target.files.length > 0) {
        attachment.value = e.target.files[0];
    } else {
        attachment.value = null;
    }
};

const sendEmailAxios = async () => {
    form.processing = true;
    try {
        const formData = new FormData();
        formData.append('id', form.id);
        formData.append('subject', form.subject);
        formData.append('message', form.message);
        if (attachment.value) {
            formData.append('attachment', attachment.value);
        }

        const response = await axios.post(route('ecommerceclaim-sendemail'), formData, {
            headers: {
                'Content-Type': 'multipart/form-data'
            }
        });

        if (response.data.success) {
            modalAlertTitle.value = '¡Envío Exitoso!';
            modalAlertMessage.value = response.data.message;
            modalAlertType.value = 'success';
            showModalAlert.value = true;

            form.message = '';
            attachment.value = null;
            if (fileInput.value) fileInput.value.value = '';

            props.complaint.status = 1;

            Inertia.reload({ only: ['complaint'] });
        } else {
            modalAlertTitle.value = 'Error de Envío';
            modalAlertMessage.value = response.data.message;
            modalAlertType.value = 'error';
            showModalAlert.value = true;
        }
    } catch (error) {
        showAlert('Ocurrió un error de red al procesar la solicitud.', 'danger');
    } finally {
        form.processing = false;
    }
}

const activeTab = ref(1);
const complaintTabs = [
    { id: 1, label: 'Ficha y Respuesta', icon: 'tabler:layout-columns', showIconOnMobile: true },
    { id: 2, label: 'Historial', icon: 'tabler:history', showIconOnMobile: true }
];

const getFileName = (path) => {
    if (!path) return '';
    const parts = path.split('/');
    const fullName = parts[parts.length - 1];
    return fullName.replace(/^\d{10}_/, '');
};

const parseUserAgent = (ua) => {
    if (!ua) return 'Desconocido';
    let browser = 'Desconocido';
    let os = 'Desconocido';

    if (ua.includes('Firefox') || ua.includes('FxiOS')) browser = 'Firefox';
    else if (ua.includes('Edg')) browser = 'Edge';
    else if (ua.includes('OPR') || ua.includes('Opera')) browser = 'Opera';
    else if (ua.includes('Chrome') || ua.includes('CriOS')) browser = 'Chrome';
    else if (ua.includes('Safari')) browser = 'Safari';

    if (ua.includes('Windows NT 10.0')) os = 'Windows 10/11';
    else if (ua.includes('Windows NT')) os = 'Windows';
    else if (ua.includes('Mac OS')) os = 'macOS';
    else if (ua.includes('Android')) os = 'Android';
    else if (ua.includes('Linux')) os = 'Linux';
    else if (ua.includes('iPhone') || ua.includes('iPad')) os = 'iOS';

    return `${browser} en ${os}`;
};
</script>

<template>

    <Head :title="'Ficha de Reclamo ' + complaint.correlative" />

    <ModalAlert :show="showModalAlert" :title="modalAlertTitle" :message="modalAlertMessage" :type="modalAlertType" :showCancel="false" confirmText="Entendido" @close="showModalAlert = false" @confirm="showModalAlert = false" />

    <BreezeAuthenticatedLayout>
        <template #header>
            <PageHeader title="Ficha de Reclamo" :subtitle="`Gestión y respuesta del ticket ${complaint.correlative}`">
                <template #actions>
                    <Buttons :href="route('claimlist')"  size="header" icon="tabler:arrow-back-up" label="Volver a la Lista" :hideLabelOnMobile="true" />
                </template>
            </PageHeader>
        </template>

        <div class="py-6 lg:py-8">
            <div class="mx-auto sm:px-6 lg:px-8 px-4 grid gap-4">

                <!-- Alertas -->
                <div v-if="isAlertActive" class="bg-green-50 border-green-600 text-sm px-4 py-3 border justify-left rounded-md flex items-center space-x-3">
                    <Icon icon="tabler:check" class="w-5 h-5 text-green-600" />
                    <span class="text-green-600 font-semibold">{{ messageAlert }}</span>
                </div>
                <div v-if="isAlertDangerActive" class="bg-red-50 border-red-600 text-sm px-4 py-3 border justify-left rounded-md flex items-center space-x-3">
                    <Icon icon="tabler:x" class="w-5 h-5 text-red-600" />
                    <span class="text-red-600 font-semibold">{{ messageAlert }}</span>
                </div>

                <div class="p-2 lg:p-4 border rounded-md">


                    <div class="flex justify-between items-center pb-4">
                        <h2 class="text-gray-700 font-bold text-sm xl:text-lg">Ficha N°: {{ complaint.correlative }}</h2>
                        <div class="flex items-center space-x-3">
                            <span class="font-semibold text-gray-700 hidden xl:inline">Estado:</span>
                            <Dropdown align="right" width="48">
                                <template #trigger>
                                    <button class="flex items-center px-4 py-2 border rounded-md font-normal text-sm focus:outline-none transition ease-in-out duration-150" :class="complaint.status == 0 ? 'bg-gray-600 text-white border-gray-600' : (complaint.status == 1 ? 'bg-blue-600 text-white border-blue-700' : 'bg-green-600 text-white border-green-600')">
                                        {{ complaint.status == 0 ? 'Pendiente' : (complaint.status == 1 ? 'En Proceso' : 'Resuelto') }}
                                        <Icon icon="tabler:chevron-down" class="w-4 h-4 ml-2" />
                                    </button>
                                </template>
                                <template #content>
                                    <button type="button" @click="updateStatus(0)" class="block w-full px-4 py-2 text-left text-sm leading-5 text-gray-700 hover:bg-gray-100 focus:outline-none focus:bg-gray-100 transition duration-150 ease-in-out">
                                        Pendiente
                                    </button>
                                    <button type="button" @click="updateStatus(1)" class="block w-full px-4 py-2 text-left text-sm leading-5 text-gray-700 hover:bg-gray-100 focus:outline-none focus:bg-gray-100 transition duration-150 ease-in-out">
                                        En Proceso
                                    </button>
                                    <button type="button" @click="updateStatus(2)" class="block w-full px-4 py-2 text-left text-sm leading-5 text-gray-700 hover:bg-gray-100 focus:outline-none focus:bg-gray-100 transition duration-150 ease-in-out">
                                        Resuelto
                                    </button>
                                </template>
                            </Dropdown>
                        </div>
                    </div>
                    <!-- TABS COMPONENT -->
                    <div class="mb-4">
                        <Tabs v-model="activeTab" :tabs="complaintTabs" :wrapOnMobile="true" />
                    </div>

                    <!-- Tab 1: Ficha y Respuesta -->
                    <div v-show="activeTab === 1" class="grid grid-cols-1 lg:grid-cols-2 gap-6 items-stretch">

                        <!-- Detalle del Reclamo -->
                        <div class="bg-white overflow-hidden border shadow-lg rounded-md p-6">

                            <!-- Auditoría de Recepción -->
                            <div class="mb-6 bg-slate-50 p-4 rounded-md border border-slate-200">
                                <h3 class="text-sm font-bold mb-2 text-slate-700 flex items-center">
                                    <Icon icon="tabler:shield-check" class="w-4 h-4 mr-1.5 text-blue-600" />
                                    Auditoría de Recepción
                                </h3>
                                <div class="grid grid-cols-1 gap-2 text-xs text-slate-600">
                                    <p><span class="font-semibold">Fecha y Hora:</span> {{ new Date(complaint.created_at).toLocaleString() }}</p>
                                    <p v-if="complaint.ip_address"><span class="font-semibold">Dirección IP:</span> {{ complaint.ip_address }}</p>
                                    <p v-if="complaint.user_agent" :title="complaint.user_agent" class="cursor-help w-max"><span class="font-semibold">Dispositivo/Navegador:</span> {{ parseUserAgent(complaint.user_agent) }}</p>
                                </div>
                            </div>

                            <div>
                                <h3 class="text-lg font-bold mb-4 border-b pb-2 text-gray-800">1. Identificación del Consumidor</h3>
                                <p class="mb-2"><span class="font-semibold text-gray-700">Nombre:</span> {{ complaint.customer_name }}</p>
                                <p class="mb-2"><span class="font-semibold text-gray-700">Documento:</span> {{ complaint.document_type }} {{ complaint.document_number }}</p>
                                <p class="mb-2"><span class="font-semibold text-gray-700">Dirección:</span> {{ complaint.address }}</p>
                                <p class="mb-2"><span class="font-semibold text-gray-700">Teléfono:</span> {{ complaint.phone }}</p>
                                <p class="mb-2"><span class="font-semibold text-gray-700">Email:</span> <a :href="'mailto:' + complaint.email" class="text-sky-600 hover:underline">{{ complaint.email }}</a></p>
                                <div v-if="complaint.is_minor == 1 || complaint.is_minor === true" class="mt-4 p-3 bg-yellow-50 rounded-md border border-yellow-200">
                                    <p class="text-sm font-semibold text-yellow-800">Menor de Edad</p>
                                    <p class="text-sm"><span class="font-semibold">Apoderado:</span> {{ complaint.parent_name }}</p>
                                </div>
                            </div>

                            <div class="mt-6">
                                <h3 class="text-lg font-bold mb-4 border-b pb-2 text-gray-800">2. Identificación del Bien Adquirido</h3>
                                <p class="mb-2"><span class="font-semibold text-gray-700">Tipo de Bien:</span> <span class="px-2 py-1 bg-green-100 text-green-800 rounded-md text-xs font-bold">{{ complaint.type_goods == 0 ? 'Producto' : 'Servicio' }}</span></p>
                                <p class="mb-2"><span class="font-semibold text-gray-700">Monto Reclamado:</span> S/ {{ complaint.amount }}</p>
                                <p class="mb-2"><span class="font-semibold text-gray-700">Factura o Boleta:</span> {{ complaint.receipt }}</p>
                                <p class="mb-2"><span class="font-semibold text-gray-700">Descripción del Bien:</span></p>
                                <p class="bg-gray-50 p-3 rounded text-sm text-gray-600 border">{{ complaint.description_goods }}</p>
                            </div>

                            <div class="mt-6">
                                <h3 class="text-lg font-bold mb-4 border-b pb-2 text-gray-800">3. Detalle del Reclamo y Pedido</h3>
                                <p class="mb-2"><span class="font-semibold text-gray-700">Tipo:</span> <span class="px-2 py-1 bg-blue-100 text-blue-800 rounded-md text-xs font-bold">{{ complaint.type_complaint == 0 ? 'Reclamo' : 'Queja' }}</span></p>

                                <div class="grid grid-cols-1 gap-4 mt-4">
                                    <div>
                                        <p class="font-semibold text-gray-700 mb-2">Detalle de lo ocurrido:</p>
                                        <p class="bg-gray-50 p-4 rounded text-sm text-gray-600 border min-h-[100px]">{{ complaint.details }}</p>
                                    </div>
                                    <div>
                                        <p class="font-semibold text-gray-700 mb-2">Pedido del consumidor:</p>
                                        <p class="bg-blue-50 p-4 rounded text-sm text-blue-800 border border-blue-100 min-h-[100px]">{{ complaint.request_text }}</p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <!-- Formulario de Respuesta -->
                        <div class="bg-white overflow-hidden border shadow-lg rounded-md p-6 flex flex-col h-full">
                            <h3 class="text-lg font-bold mb-4 border-b pb-2 text-gray-800 flex items-center shrink-0">
                                <Icon icon="tabler:mail" class="w-5 h-5 mr-2 text-sky-600" />
                                Responder al Cliente por Correo
                            </h3>

                            <div class="shrink-0 mb-6 space-y-3">
                                <p class="text-sm text-gray-500">Utilice este formulario para enviar una respuesta directa al correo registrado del cliente ({{ complaint.email }}).</p>
                                <div class="text-sm text-blue-700 bg-blue-50 p-3 rounded-md border border-blue-200 flex items-start gap-2">
                                    <Icon icon="tabler:info-circle" class="w-5 h-5 shrink-0 mt-0.5" />
                                    <span>Esta respuesta será enviada vía el correo: <strong class="font-bold">{{ fromEmail || 'No configurado' }}</strong></span>
                                </div>
                            </div>

                            <div v-if="complaint.status != 1" class="text-sm text-amber-800 bg-amber-50 p-4 rounded-md border border-amber-200 flex items-start gap-3 mb-4">
                                <Icon icon="tabler:alert-triangle" class="w-5 h-5 shrink-0 mt-0.5 text-amber-600" />
                                <span>Para poder redactar y enviar una respuesta al cliente, debe cambiar el estado del reclamo a <strong>"En Proceso"</strong> en la parte superior derecha.</span>
                            </div>

                            <form @submit.prevent="sendEmailAxios" class="flex flex-col flex-grow space-y-4" :class="{ 'opacity-60': complaint.status != 1 }">
                                <div class="shrink-0">
                                    <label class="block font-bold mb-2 text-gray-700 text-sm">Asunto del Correo <span class="text-red-600">*</span></label>
                                    <input v-model="form.subject" :disabled="complaint.status != 1" name="subject" type="text" required class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-sky-500 focus:border-sky-500 disabled:bg-gray-100 disabled:cursor-not-allowed" />
                                </div>

                                <div class="flex-grow flex flex-col">
                                    <label class="block font-bold mb-2 text-gray-700 text-sm">Mensaje de Respuesta <span class="text-red-600">*</span></label>
                                    <textarea v-model="form.message" :disabled="complaint.status != 1" required placeholder="Redacte su respuesta formal al cliente..." class="w-full flex-grow px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-sky-500 focus:border-sky-500 resize-none min-h-[150px] disabled:bg-gray-100 disabled:cursor-not-allowed"></textarea>
                                </div>

                                <div class="shrink-0 mt-4">
                                    <label class="block font-bold mb-2 text-gray-700 text-sm">Archivo Adjunto (Opcional)</label>
                                    <input type="file" ref="fileInput" :disabled="complaint.status != 1" @change="handleFileChange" accept=".pdf,.jpg,.jpeg,.png" class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-sky-500 text-sm file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-sky-50 file:text-sky-700 hover:file:bg-sky-100 disabled:opacity-70 disabled:cursor-not-allowed disabled:bg-gray-100" />
                                    <p class="text-xs text-gray-500 mt-1">Formatos permitidos: PDF, JPG, PNG. Tamaño máximo: 10MB.</p>
                                </div>

                                <div class="flex items-center justify-end mt-4 shrink-0">
                                    <Buttons color="blue" type="submit" :icon="form.processing ? 'tabler:loader-2' : 'tabler:send'" :class="{ 'opacity-50 cursor-not-allowed': form.processing || complaint.status != 1 }" :disabled="form.processing || complaint.status != 1" :label="form.processing ? 'Enviando...' : 'Enviar Respuesta al Cliente'" />
                                </div>
                            </form>
                        </div>

                    </div> <!-- Fin Tab 1 -->

                    <!-- Tab 2: Historial -->
                    <div v-show="activeTab === 2">
                        <div v-if="complaint.responses && complaint.responses.length > 0" class="bg-white overflow-hidden border shadow-lg rounded-md p-6">
                            <h3 class="text-lg font-bold mb-6 border-b pb-2 text-gray-800 flex items-center shrink-0">
                                <Icon icon="tabler:history" class="w-5 h-5 mr-2 text-gray-600" />
                                Historial de Eventos
                            </h3>

                            <div class="space-y-6">
                                <div v-for="response in complaint.responses" :key="response.id" class="relative">

                                    <!-- EVENTO TIPO CORREO -->
                                    <div v-if="response.type === 'email' || !response.type" class="border border-gray-200 rounded-lg p-5 bg-gray-50">
                                        <div class="flex flex-col lg:flex-row justify-between lg:items-center mb-3 pb-3 border-b border-gray-200 gap-2">
                                            <div>
                                                <p class="font-bold text-gray-800">{{ response.subject }}</p>
                                                <p class="text-sm text-gray-600 flex items-center mt-1">
                                                    <Icon icon="tabler:mail-forward" class="w-4 h-4 mr-1" />
                                                    Enviado por: {{ response.user ? response.user.name : 'Sistema' }}
                                                </p>
                                                <p class="text-xs text-gray-500 flex items-center mt-1">
                                                    <Icon icon="tabler:device-laptop" class="w-4 h-4 mr-1" />
                                                    {{ response.ip_address }} <span v-if="response.user_agent">| <span :title="response.user_agent" class="cursor-help">{{ parseUserAgent(response.user_agent) }}</span></span>
                                                    <span v-else>| N/A</span>
                                                </p>
                                            </div>
                                            <div class="text-right">
                                                <span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                                    {{ new Date(response.created_at).toLocaleString() }}
                                                </span>
                                            </div>
                                        </div>
                                        <div class="text-sm text-gray-700 whitespace-pre-wrap bg-white p-3 rounded border border-gray-100">{{ response.message }}</div>

                                        <div v-if="response.attachment_path" class="mt-4 pt-3 border-t border-dashed border-gray-300 flex items-center justify-between">
                                            <span class="text-sm text-gray-600 flex items-center">
                                                <Icon icon="tabler:paperclip" class="w-4 h-4 mr-1.5 text-gray-500" />
                                                Se envió archivo adjunto: <strong class="ml-1 text-gray-800">{{ getFileName(response.attachment_path) }}</strong>
                                            </span>
                                            <a :href="route('ecommerceclaim-downloadattachment', response.id)" target="_blank" class="inline-flex items-center text-sm font-semibold text-blue-600 hover:text-blue-800 bg-blue-50 px-3 py-2 rounded-md transition-colors shrink-0">
                                                <Icon icon="tabler:download" class="w-4 h-4 mr-1.5" />
                                                Descargar Archivo Adjunto
                                            </a>
                                        </div>
                                    </div>

                                    <!-- EVENTO TIPO CAMBIO DE ESTADO -->
                                    <div v-else-if="response.type === 'status_change'" class="flex items-center gap-4 py-2 px-1">
                                        <div class="w-10 h-10 rounded-full flex items-center justify-center shrink-0 shadow-sm" :class="response.message.includes('En Proceso') ? 'bg-amber-100 text-amber-600' : (response.message.includes('Resuelto') ? 'bg-emerald-100 text-emerald-600' : 'bg-gray-100 text-gray-600')">
                                            <Icon :icon="response.message.includes('En Proceso') ? 'tabler:loader' : (response.message.includes('Resuelto') ? 'tabler:check' : 'tabler:clock')" class="w-6 h-6" />
                                        </div>
                                        <div class="flex-grow">
                                            <p class="font-bold text-gray-800 text-sm">
                                                {{ response.subject }}
                                                <span class="font-normal text-gray-500 ml-2 text-xs border-l border-gray-300 pl-2">Por: {{ response.user ? response.user.name : 'Sistema' }}</span>
                                            </p>
                                            <p class="text-xs text-gray-500 mt-0.5">{{ response.message }}</p>
                                        </div>
                                        <div class="text-right shrink-0">
                                            <span class="text-xs font-medium text-gray-500">
                                                {{ new Date(response.created_at).toLocaleString() }}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div v-else class="bg-white overflow-hidden border shadow-lg rounded-md p-8 text-center min-h-[300px] flex flex-col justify-center items-center">
                            <div class="p-4 bg-gray-100 rounded-full mb-4">
                                <Icon icon="tabler:history" class="w-10 h-10 text-gray-400" />
                            </div>
                            <h3 class="text-xl font-bold text-gray-700 mb-2">No hay historial</h3>
                            <p class="text-gray-500 max-w-md mx-auto">Aún no se han enviado correos electrónicos al cliente para este reclamo.</p>
                        </div>
                    </div> <!-- Fin Tab 2 -->
                </div>
            </div>
        </div>
    </BreezeAuthenticatedLayout>
</template>
